import { inviteResultSchema, type FunctionError, type InviteResult } from '@opti-sea/core';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap, useDb } from './supabase';

/**
 * Giving and ending access (D8 crew, A3 office users): the Edge Function `invite-member` creates
 * the account when the email is new and emails the invitation; `revoke_assignment` and
 * `revoke_company_member` end access. Rights are checked on the server (PLAN §2.6).
 */

export type InviteRequest =
  | {
      kind: 'crew';
      email: string;
      fullName: string;
      vesselId: string;
      positionCode: string;
      replace: boolean;
      reason: string | null;
    }
  | { kind: 'office'; email: string; fullName: string; companyId: string };

/** A refusal of `invite-member` with its stable code (texts: `dashboard:invite.errors.*`). */
export class InviteError extends Error {
  constructor(readonly code: FunctionError | 'network') {
    super(code);
    this.name = 'InviteError';
  }
}

const KNOWN: readonly string[] = [
  'invalid_request',
  'not_signed_in',
  'not_allowed',
  'not_found',
  'position_taken',
  'server_error',
];

async function errorCode(error: unknown): Promise<InviteError> {
  if (error instanceof FunctionsHttpError) {
    const response: unknown = error.context;
    if (response instanceof Response) {
      try {
        const body = (await response.json()) as { error?: unknown };
        if (typeof body.error === 'string' && KNOWN.includes(body.error)) {
          return new InviteError(body.error as FunctionError);
        }
      } catch {
        return new InviteError('server_error');
      }
    }
    return new InviteError('server_error');
  }
  return new InviteError('network');
}

export function useInvite() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation<InviteResult, Error, InviteRequest>({
    mutationFn: async (request) => {
      const { data, error } = await db.functions.invoke('invite-member', { body: request });
      if (error !== null) {
        throw await errorCode(error);
      }
      return inviteResultSchema.parse(data);
    },
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['assignments'] }),
        client.invalidateQueries({ queryKey: ['names'] }),
        client.invalidateQueries({ queryKey: ['admin', 'members'] }),
      ]),
  });
}

export function useRevokeAssignment() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (args: { assignmentId: string; reason: string | null }) =>
      unwrap(
        await db.rpc('revoke_assignment', {
          p_assignment_id: args.assignmentId,
          p_reason: args.reason ?? undefined,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['assignments'] }),
  });
}

export function useRevokeMember() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (args: { memberId: string; reason: string | null }) =>
      unwrap(
        await db.rpc('revoke_company_member', {
          p_member_id: args.memberId,
          p_reason: args.reason ?? undefined,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'members'] }),
  });
}
